import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
  Alert,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  Poll,
  getPoll,
  usePollVotes,
  submitVote,
  hasVoted,
  markVoted,
} from '../services';

const PollVote = () => {
  const { id } = useParams<{ id: string }>();
  const [poll, setPoll] = useState<Poll | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [voted, setVoted] = useState(false);
  const [error, setError] = useState('');
  const { votes } = usePollVotes(id || '');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getPoll(id).then((p) => {
      setPoll(p);
      setLoading(false);
    });
    if (hasVoted(id)) {
      setVoted(true);
    }
  }, [id]);

  const handleVote = async () => {
    if (!id) return;
    if (poll?.multipleChoice) {
      if (selectedOptions.length === 0) return;
    } else {
      if (selectedOption === null) return;
    }
    setSubmitting(true);
    try {
      const selection = poll?.multipleChoice
        ? selectedOptions
        : selectedOption!;
      await submitVote(id, selection);
      markVoted(id);
      setVoted(true);
    } catch (e) {
      setError('Failed to submit vote. Please try again.');
    }
    setSubmitting(false);
  };

  const handleCheckboxToggle = (index: number) => {
    setSelectedOptions((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!poll) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">Poll not found</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          This poll may have been deleted or the link is invalid.
        </Typography>
      </Container>
    );
  }

  if (!poll.active && !voted) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">{poll.question}</Typography>
        <Alert severity="info" sx={{ mt: 3 }}>
          This poll is closed and no longer accepting votes.
        </Alert>
      </Container>
    );
  }

  // Count votes per option — handle both single and multiple choice votes
  const voteCounts = poll.options.map(
    (_, i) =>
      votes.filter((v) => {
        if (v.optionIndices) return v.optionIndices.includes(i);
        return v.optionIndex === i;
      }).length
  );
  const totalVotes = votes.length;

  if (voted) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          {poll.question}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          {totalVotes} vote{totalVotes !== 1 ? 's' : ''} cast
          {poll.multipleChoice && ' (multiple selections allowed)'}
        </Typography>
        {poll.options.map((option, index) => {
          const count = voteCounts[index];
          const pct = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
          return (
            <Box key={index} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="body1">{option}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {count} ({Math.round(pct)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#BF0A30',
                    borderRadius: 5,
                  },
                }}
              />
            </Box>
          );
        })}
        <Alert severity="success" sx={{ mt: 3 }}>
          Thank you for voting!
        </Alert>
      </Container>
    );
  }

  const canSubmit = poll.multipleChoice
    ? selectedOptions.length > 0
    : selectedOption !== null;

  const optionStyle = (isSelected: boolean) => ({
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    mb: 1,
    mx: 0,
    px: 2,
    py: 0.5,
    '&:hover': { backgroundColor: '#fafafa' },
    ...(isSelected && {
      borderColor: '#BF0A30',
      backgroundColor: '#fff5f5',
    }),
  });

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {poll.question}
      </Typography>
      {poll.multipleChoice && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Select all that apply
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {poll.multipleChoice ? (
        <Box>
          {poll.options.map((option, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={selectedOptions.includes(index)}
                  onChange={() => handleCheckboxToggle(index)}
                  sx={{ '&.Mui-checked': { color: '#BF0A30' } }}
                />
              }
              label={option}
              sx={optionStyle(selectedOptions.includes(index))}
              style={{ display: 'flex' }}
            />
          ))}
        </Box>
      ) : (
        <RadioGroup
          value={selectedOption !== null ? String(selectedOption) : ''}
          onChange={(e) => setSelectedOption(Number(e.target.value))}
        >
          {poll.options.map((option, index) => (
            <FormControlLabel
              key={index}
              value={String(index)}
              control={
                <Radio sx={{ '&.Mui-checked': { color: '#BF0A30' } }} />
              }
              label={option}
              sx={optionStyle(selectedOption === index)}
            />
          ))}
        </RadioGroup>
      )}
      <Button
        variant="contained"
        fullWidth
        disabled={!canSubmit || submitting}
        onClick={handleVote}
        sx={{
          mt: 3,
          py: 1.5,
          backgroundColor: '#BF0A30',
          '&:hover': { backgroundColor: '#9a0827' },
        }}
        startIcon={submitting ? <CircularProgress size={20} /> : null}
      >
        {submitting ? 'Submitting...' : 'Submit Vote'}
      </Button>
    </Container>
  );
};

export default PollVote;
